import {
  SovColumnId,
  SovColumnProps,
  SovColumnSource,
  sourceOfColumnId,
} from "./AddressCardColumns";

// A view is a layout of the client's column catalog: which columns show, in
// what order, and how they're headed. The catalog itself (getAddressColumns)
// stays the single source of truth for what a column is and how it's edited;
// views only pick from it by id. A column id a client's catalog lacks is
// skipped, so one view definition serves every client.
export type SovViewId = "default" | "vendor_compare";

export type SovView = {
  id: SovViewId;
  label: string;
  description: string;
  layout: (catalog: SovColumnProps[]) => SovColumnProps[];
};

export const DEFAULT_SOV_VIEW_ID: SovViewId = "default";

const SOURCE_LABELS: Record<SovColumnSource, string> = {
  broker: "Broker",
  derived: "Derived",
  geo: "Geo",
  rms: "RMS",
  air: "AIR",
  spatialkat: "KatRisk",
  hazardhub: "HazardHub",
  nearmap: "Nearmap",
  cape: "Cape",
  sindri: "Sindri",
  terms: "Terms",
};

const pickColumns = (
  catalog: SovColumnProps[],
  ids: readonly SovColumnId[]
): SovColumnProps[] => {
  const byId = new Map(catalog.map((c) => [c.id, c]));
  return ids.flatMap((id) => {
    const column = byId.get(id);
    return column ? [column] : [];
  });
};

// Address and value columns every view leads with, in this order.
const IDENTITY_COLUMN_IDS: readonly SovColumnId[] = [
  "derived.map",
  "broker.premises_number",
  "broker.building_number",
  "broker.street_address",
  "broker.city",
  "broker.state",
  "broker.zip",
  "broker.county",
  "broker.country_code",
];
const COVERAGE_COLUMN_IDS: readonly SovColumnId[] = [
  "broker.tiv",
  "broker.building_value",
  "broker.bpp_value",
  "broker.bi_incl_ee_value",
  "broker.bi_excl_ee_value",
];

type CompareEntry = {
  id: SovColumnId;
  label: string;
  // A read-only copy of a column the view also shows elsewhere.
  readOnlyCopy?: boolean;
};

// One attribute as reported by several sources. The source name heads each
// column unless the entry overrides it.
type CompareCluster = {
  label: string;
  columns: readonly (SovColumnId | CompareEntry)[];
};

const COMPARE_CLUSTERS: readonly CompareCluster[] = [
  {
    label: "Replacement Cost",
    columns: [
      // Edited under coverage; repeated here beside the estimates of it.
      { id: "broker.building_value", label: "Broker", readOnlyCopy: true },
      { id: "hazardhub.replacement_cost", label: "HazardHub (assessor)" },
      { id: "sindri.luminor_rc", label: "Luminor RC" },
      // Building value as a share of the Luminor RC.
      { id: "sindri.itv_ratio", label: "RC ITV" },
    ],
  },
  {
    label: "Year Built",
    columns: ["broker.year_built", "hazardhub.year_built", "cape.year_built"],
  },
  {
    label: "Construction",
    columns: [
      "broker.construction_code",
      "hazardhub.construction_type",
      "cape.construction_type",
    ],
  },
  {
    label: "Stories",
    columns: [
      "broker.number_of_stories",
      "hazardhub.number_of_stories",
      "cape.number_of_stories",
    ],
  },
  {
    label: "# Buildings",
    columns: [
      "broker.number_of_buildings",
      "hazardhub.number_of_buildings",
      "cape.structure_count",
    ],
  },
  {
    label: "Sqft",
    columns: ["broker.square_footage", "hazardhub.square_footage"],
  },
  {
    label: "Occupancy",
    columns: ["broker.sic_code", "hazardhub.use_code"],
  },
  {
    label: "Roof Covering",
    columns: [
      "broker.roof_covering",
      "nearmap.roof_material",
      "cape.roof_covering",
    ],
  },
  {
    label: "Roof Geometry",
    columns: [
      "broker.roof_geometry",
      "nearmap.roof_shapes",
      "cape.roof_geometry",
    ],
  },
  {
    label: "Roof Age (yr)",
    columns: ["nearmap.roof_age_years", "cape.roof_age_years"],
  },
  {
    label: "Roof Replaced",
    columns: ["broker.roofing_year", "cape.roof_replacement_year"],
  },
  {
    label: "Solar",
    columns: ["nearmap.solar_panels", "cape.solar_panel"],
  },
  {
    label: "Flood Zone",
    columns: [
      "broker.flood_zone",
      { id: "geo.flood_zone", label: "FEMA" },
      "spatialkat.fema_flood_zone",
    ],
  },
];

// The id of a read-only copy. AG Grid needs every column id in a layout to be
// unique, and the copy keeps no editor, so editing stays in one place.
export const readOnlyCopyId = (
  id: SovColumnId,
  clusterLabel: string
): SovColumnId => `${id}~${clusterLabel.toLowerCase().replaceAll(" ", "_")}`;

const readOnlyCopy = (
  column: SovColumnProps,
  clusterLabel: string
): SovColumnProps => ({
  ...column,
  id: readOnlyCopyId(column.id, clusterLabel),
  editor: undefined,
  agEditor: undefined,
  agEditorParams: undefined,
  bulkEdit: false,
  customAction: undefined,
});

const compareClusterColumns = (
  catalog: SovColumnProps[],
  cluster: CompareCluster
): SovColumnProps[] => {
  const entries: CompareEntry[] = cluster.columns.map((entry) =>
    typeof entry === "string"
      ? { id: entry, label: SOURCE_LABELS[sourceOfColumnId(entry)] }
      : entry
  );
  const picked = pickColumns(
    catalog,
    entries.map((e) => e.id)
  );
  // A lone source isn't a comparison; drop the cluster for this client.
  if (picked.length < 2) return [];
  return picked.map((column) => {
    const entry = entries.find((e) => e.id === column.id);
    const placed: SovColumnProps = {
      ...column,
      headerLabel: entry?.label ?? column.columnName,
      headerGroup: cluster.label,
      collapsibleGroup: undefined,
    };
    return entry?.readOnlyCopy ? readOnlyCopy(placed, cluster.label) : placed;
  });
};

export const SOV_VIEWS: readonly SovView[] = [
  {
    id: "default",
    label: "Default",
    description: "Every column, with vendor data in collapsible groups.",
    layout: (catalog) => catalog,
  },
  {
    id: "vendor_compare",
    label: "Vendor compare",
    description:
      "Each attribute as the broker, HazardHub, Cape, Nearmap and Sindri report it, side by side.",
    layout: (catalog) => [
      ...pickColumns(catalog, IDENTITY_COLUMN_IDS),
      ...pickColumns(catalog, COVERAGE_COLUMN_IDS),
      ...COMPARE_CLUSTERS.flatMap((cluster) =>
        compareClusterColumns(catalog, cluster)
      ),
    ],
  },
];

export const sovViewById = (id: SovViewId): SovView => {
  const view = SOV_VIEWS.find((v) => v.id === id);
  if (!view) throw new Error(`Unknown SOV view: ${id}`);
  return view;
};

export const isSovViewId = (value: unknown): value is SovViewId =>
  SOV_VIEWS.some((v) => v.id === value);
